import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CampText from '../CampText';

/**
 * Passa 5 — el pla d'acció.
 *
 * Quan s'arriba aquí la major part del pla ja està escrita: cada acció surt del
 * «què cal fer ara» d'una fita que controles, o de la palanca d'influència d'una
 * que no. Per això aquí **no es torna a escriure res**: es recullen aquelles
 * respostes, s'agrupen per quan toca fer-les, s'hi posa un responsable i s'hi
 * afegeix el que no hagi sortit de cap fita.
 *
 * És l'única pantalla pensada per sortir en paper: és el document que se'n duen.
 */

const HORITZONS = ['now', 'short', 'medium'];

const ActionPlanStep = ({ exercise, actions, milestones, onCreate, onPatch, onDelete }) => {
  const { t } = useTranslation();
  const [exportant, setExportant] = useState(false);

  const perFita = useMemo(() => {
    const m = {};
    milestones.forEach(f => { m[f.id] = f; });
    return m;
  }, [milestones]);

  /* Suggeriments: el que s'ha escrit a la passa 4 i encara no és al pla.
     Es comparen pel text perquè una acció es pot editar després d'afegir-la i
     no volem que reaparegui com a suggeriment cada cop que es retoca. */
  const suggeriments = useMemo(() => {
    const jaHi = new Set(actions.map(a => (a.description || '').trim().toLowerCase()));
    const fora = [];
    milestones
      .filter(m => m.is_critical)
      .sort((a, b) => (a.position - b.position) || (a.id - b.id))
      .forEach(m => {
        const camp = m.control_level === 'none' ? 'influence_levers' : 'immediate_steps';
        const text = (m[camp] || '').trim();
        if (text && !jaHi.has(text.toLowerCase())) {
          fora.push({ milestone: m, camp, text });
        }
      });
    return fora;
  }, [actions, milestones]);

  const perHoritzo = (h) => actions
    .filter(a => a.horizon === h)
    .sort((a, b) => (a.position - b.position) || (a.id - b.id));

  const exportaPDF = async () => {
    setExportant(true);
    try {
      // jsPDF es carrega només quan cal i des del nostre propi servidor, com
      // ja fa el mapeig estratègic. Cap CDN: la política de contingut del
      // servidor no el deixaria passar.
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });

      const marge = 18;
      const ample = pdf.internal.pageSize.getWidth() - marge * 2;
      const alt = pdf.internal.pageSize.getHeight();
      let y = marge;

      const salt = (necessari) => {
        if (y + necessari > alt - marge) { pdf.addPage(); y = marge; }
      };

      const escriu = (text, mida, estil, espai) => {
        pdf.setFont('helvetica', estil);
        pdf.setFontSize(mida);
        const linies = pdf.splitTextToSize(text, ample);
        linies.forEach(l => {
          salt(mida * 0.5);
          pdf.text(l, marge, y);
          y += mida * 0.5;
        });
        y += espai;
      };

      escriu(exercise.name || t('backcasting.untitledExercise'), 18, 'bold', 2);
      pdf.setDrawColor(200);
      pdf.line(marge, y, marge + ample, y);
      y += 6;

      escriu(`${t('backcasting.actionPlan')} · ${t('backcasting.horizon')}: ${exercise.horizon_label || '—'}`,
        10, 'normal', 4);

      if (exercise.vision_statement) {
        escriu(t('backcasting.visionLabel'), 9, 'bold', 1);
        escriu(exercise.vision_statement, 10, 'normal', 5);
      }

      HORITZONS.forEach(h => {
        const files = perHoritzo(h);
        if (!files.length) return;
        salt(20);
        escriu(t(`backcasting.horizonGroup.${h}`).toUpperCase(), 9, 'bold', 2);
        files.forEach(a => {
          escriu(`• ${a.description || ''}`, 11, 'normal', 1);
          const peu = [
            a.owner && `${t('backcasting.owner')}: ${a.owner}`,
            a.due_label && `${t('backcasting.due')}: ${a.due_label}`,
            a.milestone_id && perFita[a.milestone_id] &&
              `${t('backcasting.fromMilestone')}: ${perFita[a.milestone_id].title || ''}`
          ].filter(Boolean).join('   ');
          if (peu) escriu(peu, 8, 'italic', 2);
          else y += 2;
        });
        y += 3;
      });

      const nom = (exercise.name || 'backcasting')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'backcasting';
      pdf.save(`${nom}-pla-accio.pdf`);
    } catch (error) {
      console.error('Error exporting action plan:', error);
      toast.error(t('backcasting.exportError'));
    } finally {
      setExportant(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">{t('backcasting.actionPlan')}</h3>
          <p className="mt-1 text-sm text-neutral-600">
            {exercise.name}
            {exercise.horizon_label ? ` · ${exercise.horizon_label}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={exportaPDF}
            disabled={exportant || actions.length === 0}
            className="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            {exportant ? t('backcasting.exporting') : t('backcasting.exportPdf')}
          </button>
          <button
            type="button"
            /* Igual que a la línia de temps: `description` és obligatòria a
               l'esquema i l'acció neix en blanc, o sigui que s'hi ha d'enviar
               la cadena buida. Sense això, 422 i el botó no feia res. */
            onClick={() => onCreate({ description: '', horizon: 'now', position: actions.length })}
            className="rounded-md bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            {t('backcasting.addAction')}
          </button>
        </div>
      </div>

      {suggeriments.length > 0 && (
        <section className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            {t('backcasting.suggestionsTitle')}
          </h4>
          <p className="mt-1 text-xs text-neutral-500">{t('backcasting.suggestionsLead')}</p>
          <ul className="mt-3 space-y-2">
            {suggeriments.map(s => (
              <li key={`${s.milestone.id}-${s.camp}`}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2">
                <div className="min-w-[16rem] flex-1">
                  <p className="text-sm text-neutral-800">{s.text}</p>
                  <p className="mt-0.5 text-xs text-neutral-400">
                    {t('backcasting.fromMilestone')}: {s.milestone.title || t('backcasting.untitledMilestone')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onCreate({
                    description: s.text,
                    milestone_id: s.milestone.id,
                    horizon: s.camp === 'immediate_steps' ? 'now' : 'short',
                    position: actions.length
                  })}
                  className="rounded-md border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  {t('backcasting.addToPlan')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {actions.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-10 text-center text-sm text-neutral-500">
          {t('backcasting.planEmpty')}
        </p>
      ) : (
        HORITZONS.map(h => {
          const files = perHoritzo(h);
          if (!files.length) return null;
          return (
            <section key={h}>
              <h4 className="mb-2 border-b border-neutral-200 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                {t(`backcasting.horizonGroup.${h}`)}
              </h4>
              <ul className="divide-y divide-neutral-200">
                {files.map(accio => (
                  <li key={accio.id} className="grid gap-3 py-3 md:grid-cols-[1fr_150px_130px_auto] md:items-start">
                    <div>
                      <CampText
                        value={accio.description}
                        onSave={(v) => onPatch(accio.id, { description: v || t('backcasting.untitledAction') })}
                        multiline
                        rows={2}
                        inputClassName="text-sm"
                      />
                      {accio.milestone_id && perFita[accio.milestone_id] && (
                        <p className="mt-1 text-xs text-neutral-400">
                          {t('backcasting.fromMilestone')}: {perFita[accio.milestone_id].title || t('backcasting.untitledMilestone')}
                        </p>
                      )}
                    </div>
                    <CampText
                      value={accio.owner}
                      onSave={(v) => onPatch(accio.id, { owner: v })}
                      placeholder={t('backcasting.owner')}
                      inputClassName="text-sm"
                    />
                    <CampText
                      value={accio.due_label}
                      onSave={(v) => onPatch(accio.id, { due_label: v })}
                      placeholder={t('backcasting.due')}
                      inputClassName="text-sm"
                    />
                    <div className="flex items-start gap-1">
                      <select
                        value={accio.horizon}
                        onChange={(e) => onPatch(accio.id, { horizon: e.target.value })}
                        aria-label={t('backcasting.horizonGroupLabel')}
                        className="rounded-md border border-neutral-300 bg-white px-2 py-2 text-xs text-neutral-700 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
                      >
                        {HORITZONS.map(o => (
                          <option key={o} value={o}>{t(`backcasting.horizonShort.${o}`)}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => onDelete(accio.id)}
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                        className="rounded p-2 text-neutral-400 hover:bg-neutral-100 hover:text-error-dark"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
};

export default ActionPlanStep;
